import { Component, inject, input, output } from '@angular/core';
import { SelectionArguments } from '../../../shared/models/selection-arguments';

import { OfflineFormsClient } from '../../offline-orders/offline-forms.client';
import {
  IFormsProvider,
  LFOfflineOrderSourceType,
  LiveForm,
  OrderData,
  ResourceState,
  StyleHelper
} from '@lfw/roesberg.liveforms.core';
import { RouterModule } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { OpenInNewTabDirective } from '../../../route/open-in-new-tab.directive';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';

import { ProjectsService } from '../../../projects/projects.service';
import { tap, take, forkJoin, switchMap, mergeMap, of } from 'rxjs';
import { OnlineFormsProvider } from '../online-forms.provider';
import { OfflineFormsProvider } from '../../offline-orders/offline-forms.provider';
import { OrdersService } from '../../orders.service';
import { catchError } from 'rxjs/operators';
import { ErrorDialogComponent } from '../../../shared/dialogs/error-dialog/error-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '../../../shared/dialogs/confirm-dialog/confirm-dialog.component';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { OnlineOrdersListActionsComponent } from '../online-order-list-actions/online-order-list-actions.component';

@Component({
  selector: 'lfw-order-list-item',
  templateUrl: './order-list-item.component.html',
  styleUrls: ['./order-list-item.component.css'],
  providers: [{ provide: IFormsProvider, useClass: OnlineFormsProvider }, OfflineFormsProvider],
  imports: [
    RouterModule,
    MatIcon,
    MatButtonModule,
    OpenInNewTabDirective,
    MatTooltipModule,
    MatMenuModule,
    OnlineOrdersListActionsComponent
  ]
})
export class OrderListItemComponent {
  order = input.required<OrderData>();
  projectSelected = output<SelectionArguments>();
  refreshOrders = output<void>();

  private dialog = inject(MatDialog);
  private translate = inject(TranslateService);
  private offlineFormsClient = inject(OfflineFormsClient);
  private projectService = inject(ProjectsService);
  private orderService = inject(OrdersService);
  private formService = inject(IFormsProvider<LiveForm>);
  private offlineFormService = inject(OfflineFormsProvider);
  private errorHandling = inject(ErrorHandlerService);

  get userCanCancel() {
    return this.projectService.currentProjectUserCanCancelOrder;
  }

  getOrderStateText() {
    return this.order().orderState.text ?? '';
  }

  getSubStateText() {
    return this.order().orderSubState?.text ?? '';
  }
  getScenarioText() {
    return this.order().scenario?.name ?? '';
  }

  getOrderStateStyle() {
    return this.order().orderState.getStyle();
  }

  getSubStateStyle() {
    return this.order().orderSubState?.getStyle();
  }

  getScenarioStyle() {
    return StyleHelper.getColorStyle(this.order().scenario?.color ?? '', '000000') ?? '';
  }

  checkoutOrder() {
    this.orderService
      .checkoutOrder(this.projectService.currentProjectId, this.order().id)
      .pipe(
        tap(async (form) => {
          await this.offlineFormsClient.saveOfflineOrder(form, LFOfflineOrderSourceType.USER_SELECTED);
          this.refreshOrders.emit();
        }),
        mergeMap((form: LiveForm) =>
          forkJoin([
            of(form),
            ...form.attachedResources.map((data) =>
              this.formService.loadFormResource(data.fileId, form.id, this.projectService.currentProjectId)
            )
          ])
        ),
        switchMap(([form, ...blobs]) =>
          forkJoin(
            blobs.map((blob, index) =>
              this.offlineFormService.upload({
                ...form.attachedResources[index],
                data: blob,
                saveId: '',
                state: ResourceState.Unchanged,
                orderId: form.id
              })
            )
          )
        ),
        catchError((httpError) => {
          this.handleCheckoutError(httpError);
          return of(httpError);
        }),
        take(1)
      )
      .subscribe();
  }

  private handleCheckoutError(httpError: any) {
    const errorCode = this.errorHandling.getErrorCode(httpError || 'UNKNOWN');
    const message = this.errorHandling.getErrorMessage(httpError);
    const title = this.translate.instant('DIALOGS.WARNING');
    this.dialog
      .open(errorCode === 'ORDEROWNLOCK' ? ConfirmDialogComponent : ErrorDialogComponent, {
        data: { title, message },
        closeOnNavigation: true,
        disableClose: true
      })
      .afterClosed()
      .pipe(
        take(1),
        tap((force) => {
          if (force) {
            // this.loadForm(force);
          } else {
            // this.close();
          }
        })
      )
      .subscribe();
  }

  finishOrder() {
    throw new Error('Method not implemented.');
  }
  cancelOrder() {
    throw new Error('Method not implemented.');
  }

  showInfo(event: Event) {
    event.stopImmediatePropagation();
  }

  getLink() {
    return [this.order().isEditAllowed ? 'edit' : 'view', this.order().id];
  }
}
